import { useEffect } from 'react';
import {
  createGeofence, linkGeofenceToDevice, getGeofenceEvents, geocode,
} from './traccar';
import { getLoads, putLoad, getDetentionSettings, chargeAmount, withStatus } from './loadStore';

const ACTIVE = ['Dispatched', 'At Pickup', 'Loaded', 'In Transit', 'At Delivery'];

// Which stops can be geofenced (have enough address to geocode)
const geoStops = (load) => (load.stops || []).filter((s) => (s.zip || (s.city && s.state) || s.address));

// Arms a load: geocode every stop, create + link a geofence per stop.
async function armLoad(load, settings) {
  if (!load.vehicleId || !load.autoTrack || (load.geo && load.geo.armed)) return null;
  const stops = geoStops(load);
  if (stops.length === 0) return null;

  const geo = { ...(load.geo || {}), stops: { ...((load.geo || {}).stops || {}) }, lastEventId: (load.geo || {}).lastEventId || 0 };
  const radius = settings.radiusMeters || 300;
  let pc = 0, dc = 0, created = 0;

  for (const s of stops) {
    const n = s.type === 'pickup' ? ++pc : ++dc;
    if (geo.stops[s.id] && geo.stops[s.id].geofenceId) continue;
    const pt = await geocode({ address: s.address, city: s.city, state: s.state, zip: s.zip });
    if (!pt) continue;
    const label = `${s.type === 'pickup' ? 'PU' : 'DEL'}${n} ${load.billNumber}`;
    const g = await createGeofence(label, pt.lat, pt.lng, radius);
    if (g && g.id) {
      await linkGeofenceToDevice(load.vehicleId, g.id);
      geo.stops[s.id] = { geofenceId: g.id, type: s.type, seq: n, city: s.city || '', appt: s.early || '', lat: pt.lat, lng: pt.lng, arrival: null, departure: null, detentionHrs: 0 };
      created++;
    }
  }
  geo.armed = Object.keys(geo.stops).length > 0;
  return created > 0 ? { ...load, geo } : null;
}

// Detention per stop, billed separately. The clock starts (freeHours) after the
// stop's appointment time — e.g. 3 hours after the appointment. detentionHrs on
// each stop already excludes that free window, so the whole amount is billable.
function stopDetentionHrs(st, departureAt, settings) {
  const freeMs = (settings.freeHours || 3) * 3600000;
  const base = st.appt ? new Date(st.appt).getTime() : new Date(st.arrival).getTime();
  const cutoff = base + freeMs; // detention starts here
  const start = Math.max(new Date(st.arrival).getTime(), cutoff);
  return Math.max(0, (new Date(departureAt).getTime() - start) / 3600000);
}

// Rebuild one detention charge line per stop that has detention
function rebuildDetention(load, settings) {
  const charges = (load.charges || []).filter((c) => !/^DET-/.test(c.code || ''));
  Object.values(load.geo.stops).forEach((s) => {
    if ((s.detentionHrs || 0) > 0.02) {
      const code = `DET-${s.type === 'pickup' ? 'PU' : 'DEL'}${s.seq}`;
      charges.push({
        code,
        description: `${s.type === 'pickup' ? 'Pickup' : 'Delivery'} #${s.seq}${s.city ? ' — ' + s.city : ''} detention (${s.detentionHrs.toFixed(1)} hr, starts ${settings.freeHours}h after appt)`,
        qty: 1,
        rate: +(s.detentionHrs * (settings.ratePerHour || 0)).toFixed(2),
      });
    }
  });
  return charges;
}

// Process geofence events for one armed load (any number of stops)
async function processLoad(load, settings) {
  if (!load.geo || !load.geo.armed || !load.vehicleId) return null;
  let events;
  try { events = await getGeofenceEvents(load.vehicleId, 4 * 24 * 60); } catch { return null; }
  const byGeofence = {};
  Object.entries(load.geo.stops).forEach(([sid, s]) => { byGeofence[s.geofenceId] = sid; });

  const relevant = events
    .filter((e) => byGeofence[e.geofenceId] != null)
    .filter((e) => (e.id || 0) > (load.geo.lastEventId || 0))
    .sort((a, b) => new Date(a.eventTime) - new Date(b.eventTime));
  if (relevant.length === 0) return null;

  const next = { ...load, geo: { ...load.geo, stops: JSON.parse(JSON.stringify(load.geo.stops)) }, statusHistory: [...(load.statusHistory || [])] };
  let changed = false;
  const setStat = (s, at) => { if (next.status !== s) { next.statusHistory.push({ status: s, at, note: 'auto (geofence)' }); next.status = s; } };

  relevant.forEach((e) => {
    const sid = byGeofence[e.geofenceId];
    const st = next.geo.stops[sid];
    const at = e.eventTime;
    if (e.type === 'geofenceEnter' && !st.arrival) {
      st.arrival = at;
      setStat(st.type === 'pickup' ? 'At Pickup' : 'At Delivery', at);
      changed = true;
    } else if (e.type === 'geofenceExit' && st.arrival && !st.departure) {
      st.departure = at;
      st.detentionHrs = stopDetentionHrs(st, at, settings);
      next.charges = rebuildDetention(next, settings);
      // status after leaving: last pickup done → In Transit; last delivery done → Delivered
      const stopsOfType = Object.values(next.geo.stops).filter((x) => x.type === st.type);
      const allDone = stopsOfType.every((x) => x.departure);
      if (st.type === 'pickup') setStat(allDone ? 'In Transit' : 'Loaded', at);
      else setStat(allDone ? 'Delivered' : 'In Transit', at);
      changed = true;
    }
    next.geo.lastEventId = Math.max(next.geo.lastEventId || 0, e.id || 0);
  });

  return changed ? next : (next.geo.lastEventId !== load.geo.lastEventId ? next : null);
}

export default function DispatchAutomation() {
  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      try {
        const [loads, settings] = await Promise.all([getLoads(), getDetentionSettings()]);
        if (cancelled || !settings.autoTrack) return;

        for (const load of loads) {
          if (!ACTIVE.includes(load.status) && load.status !== 'Available') continue;
          if (!load.vehicleId || !load.autoTrack) continue;

          // arm if needed
          if (!load.geo || !load.geo.armed) {
            const armed = await armLoad(load, settings);
            if (armed) { await putLoad(armed); continue; }
          } else {
            const updated = await processLoad(load, settings);
            if (updated) await putLoad(updated);
          }
          if (cancelled) return;
        }
      } catch { /* try again next round */ }
    };

    tick();
    const t = setInterval(tick, 60000); // every minute
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return null;
}
